import React, { createContext, useContext, ReactNode } from "react";
import { useValidation } from "../hooks/useValidation";

// Interface for the context value
interface PremiumContextType {
  isPremium: boolean;
  isLoading: boolean;
  premiumFeatures: {
    customTerraformBucket: boolean;
    multiCloudSupport: boolean;
    advancedAnalytics: boolean;
  };
}

// Create the context
const PremiumContext = createContext<PremiumContextType | undefined>(undefined);

// Hook to use the premium context
export const usePremium = (): PremiumContextType => {
  const context = useContext(PremiumContext);
  if (!context) {
    throw new Error("usePremium must be used within a PremiumProvider");
  }
  return context;
};

// Props for the provider
interface PremiumProviderProps {
  children: ReactNode;
}

// Premium provider component
export const PremiumProvider: React.FC<PremiumProviderProps> = ({
  children,
}) => {
  // Use shared validation hook to prevent duplicate API calls
  const { data, isLoading } = useValidation();

  // Determine if user has premium subscription (fallback to false if no data)
  const isPremium = data?.subscription_type === "premium" || false;

  // Define premium features based on subscription type
  const premiumFeatures = {
    customTerraformBucket: isPremium,
    multiCloudSupport: isPremium,
    advancedAnalytics: isPremium,
  };

  // For development/testing, you can override the API response
  // const isPremium = true; // Uncomment to test premium features

  return (
    <PremiumContext.Provider
      value={{
        isPremium,
        isLoading,
        premiumFeatures,
      }}
    >
      {children}
    </PremiumContext.Provider>
  );
};

// Simple hook that returns just the isPremium boolean for backward compatibility
export const useIsPremium = () => {
  const { isPremium } = usePremium();
  return isPremium;
};

export default PremiumContext;
